const CONTEXT_ID_KEYS = ['course_id', 'adoption_id', 'canonical_institution_id', 'lti_course_id'];
const ANALYTICS_ROLES = ['internal', 'educator', 'pending_educator'];
const RESERVED_PARAM_KEYS = new Set([
  'event', 'surface', 'user_id', 'role', 'canonical_institution_name', ...CONTEXT_ID_KEYS,
]);
const PERSONAL_PARAM_KEYS = new Set([
  'email', 'emailaddress', 'name', 'firstname', 'lastname', 'fullname', 'username', 'displayname',
]);

const SIGN_IN_SESSION_KEY = 'viewer_analytics_sign_in';
const TEXTBOOK_BROWSE_SESSION_PREFIX = 'viewer_analytics_textbook_browse_';

let actor = {};
let context = {};
let lastPagePath = null;

function positiveInteger(value) {
  const number = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  return Number.isSafeInteger(number) && number > 0 ? number : undefined;
}

function compact(values) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value != null));
}

export function setActor(currentUser) {
  actor = {};
  setContext(null);

  const userId = positiveInteger(currentUser?.id);
  if (userId === undefined) { return; }

  const roles = Array.isArray(currentUser.roles) ? currentUser.roles : [];
  actor = {
    user_id: userId,
    role: ANALYTICS_ROLES.find(role => roles.includes(role)) || 'student',
  };
}

export function setContext(authorizationResponse) {
  context = {};
  if (!authorizationResponse) { return; }

  CONTEXT_ID_KEYS.forEach(key => {
    const id = positiveInteger(authorizationResponse[key]);
    if (id !== undefined) { context[key] = id; }
  });

  const name = authorizationResponse.canonical_institution_name;
  if (typeof name === 'string' && name.trim()) {
    context.canonical_institution_name = name.trim();
  }
}

// Callers must use non-PII scalar parameters, never personal/free-text user content.
// Reject identity fields, email values, and nested data as an additional safeguard.
function eventParams(params) {
  return Object.fromEntries(Object.entries(params || {}).filter(([key, value]) => {
    const normalizedKey = key.replace(/[^a-z0-9]/gi, '').toLowerCase();
    const personalKey = normalizedKey.replace(/^user/, '');
    if (RESERVED_PARAM_KEYS.has(key) || normalizedKey === 'userproperties' ||
        PERSONAL_PARAM_KEYS.has(normalizedKey) || PERSONAL_PARAM_KEYS.has(personalKey)) {
      return false;
    }
    if (typeof value === 'string') { return !/[^\s@]+@[^\s@]+/.test(value); }
    return typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value));
  }));
}

function push(payload) {
  if (typeof window === 'undefined') { return; }

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(compact({ ...payload, ...actor, ...context, surface: 'viewer' }));
  } catch {
    // Analytics must never interrupt the reader if the queue is unavailable.
  }
}

export function trackEvent(event, params) {
  if (typeof event !== 'string' || !event.trim()) { return; }
  push({ ...eventParams(params), event });
}

function oncePerSession(key, callback) {
  try {
    if (sessionStorage.getItem(key) === '1') { return; }
    sessionStorage.setItem(key, '1');
  } catch {
    // sessionStorage unavailable — still run the callback once (Scholar parity).
  }
  callback();
}

export function currentPagePath() {
  if (typeof window === 'undefined') { return ''; }
  return `${window.location.pathname}${window.location.search}`;
}

export function resetPageViewDedupe() {
  lastPagePath = null;
}

export function trackPageView(path) {
  if (typeof window === 'undefined' || typeof document === 'undefined') { return; }

  const pagePath = path == null ? currentPagePath() : path;
  if (typeof pagePath !== 'string' || !pagePath) { return; }

  push({
    event: 'virtual_page_view',
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackPageViewIfChanged(path) {
  const pagePath = path == null ? currentPagePath() : path;
  if (pagePath === lastPagePath) { return; }
  lastPagePath = pagePath;
  trackPageView(pagePath);
}

export function trackSignInOnce() {
  oncePerSession(SIGN_IN_SESSION_KEY, () => {
    trackEvent('sign_in');
  });
}

export function trackTextbookBrowseOnce(textbookId, textbookTitle) {
  const id = positiveInteger(textbookId);
  if (id === undefined) { return; }
  if (typeof textbookTitle !== 'string' || !textbookTitle.trim()) { return; }

  oncePerSession(`${TEXTBOOK_BROWSE_SESSION_PREFIX}${id}`, () => {
    trackEvent('textbook_browse', { textbook_id: id, textbook_title: textbookTitle.trim() });
  });
}

export function trackTextbookEvent(event, textbookId, textbookTitle, params) {
  const id = positiveInteger(textbookId);
  if (id === undefined) { return; }
  if (typeof textbookTitle !== 'string' || !textbookTitle.trim()) { return; }

  trackEvent(event, {
    textbook_id: id,
    textbook_title: textbookTitle.trim(),
    ...eventParams(params),
  });
}

function engagementElementId(elementId) {
  if (typeof elementId !== 'string' || !elementId.trim()) { return undefined; }
  return elementId.trim();
}

export function trackSectionViewSubmit(textbookId, textbookTitle, elementId) {
  const trimmedElementId = engagementElementId(elementId);
  if (trimmedElementId === undefined) { return; }

  trackTextbookEvent('section_view', textbookId, textbookTitle, { element_id: trimmedElementId });
}

export function trackVideoViewSubmit(textbookId, textbookTitle, elementId) {
  const trimmedElementId = engagementElementId(elementId);
  if (trimmedElementId === undefined) { return; }
  if (/^https?:\/\//i.test(trimmedElementId)) { return; }

  trackTextbookEvent('video_view', textbookId, textbookTitle, { element_id: trimmedElementId });
}

export function trackAnnotationCreate(textbookId, textbookTitle, elementId) {
  const trimmedElementId = engagementElementId(elementId);
  const params = trimmedElementId === undefined ? {} : { element_id: trimmedElementId };

  trackTextbookEvent('annotation_create', textbookId, textbookTitle, params);
}

export function trackHighlightsPanelOpen(textbookId, textbookTitle) {
  trackTextbookEvent('highlights_panel_open', textbookId, textbookTitle);
}
